import {combineLatest, combineLatest as observableCombineLatest, Observable, of, of as observableOf, Subject, switchMap, timer} from 'rxjs';

import {catchError, map, mergeMap, take, takeUntil, tap} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input} from '@angular/core';
import {DeviceSlot} from '../../types/devices/device-slot';
import {DeviceSlotId, DeviceSlotReservationId, ProjectId, UserId} from '../../types/ids';
import {WebmateEvent} from '../../types/base-types';
import {UserService} from '../../services/user.service';
import {CreateOrDeleteDevicePermission, ManageSlotsPermission, SetDeviceSlotProperties,} from '../../constants/permission.constants';
import {DeviceSlotService} from '../../services/device/device-slot.service';
import {DeployableService} from '../../services/device/deployable.service';
import {
    AdvertisedDeployable,
    constructRequirementsForDeployableInSlot,
    Deployable,
    deployableSortingFunction, isAvailable
} from '../../types/devices/device-template';
import {Device} from '../../types/devices/device';
import {DeviceService} from '../../services/device/device.service';
import {UserInfo} from '../../types/user/user-info';
import {FeatureService} from '../../services/feature.service';
import {DeviceSlotReservationService} from '../../services/device/device-slot-reservation.service';
import {DeviceSlotReservation, reservationSortingFunction} from '../../types/devices/device-slot-reservation';
import {WmNotificationService} from '../notification/wm-notification.service';
import {ProjectService} from '../../services/project.service';
import {Project} from '../../types/interfaces/project.interface';
import {LogoResolverService} from '@webmate-web-core/shared/services/logo-resolver.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DevicePropertyNames, getPlatformFamily, PlatformFamily} from '@webmate-web-core/shared/types/devices/device-properties';
import {capitalize} from '@webmate-web-core/shared/utils/utils';
import {ScriptManagerComponent, ScriptManagerToolConfig} from '@webmate-web-core/shared/tools/scriptmanager/script-manager.component';
import {ModalToolContainerService} from '../../tools/modaltoolcontainer/modaltoolcontainer.service';
import {
    SlotMalfunctionReporterComponent, SlotMalfunctionReporterToolConfig
} from "@webmate-web-core/shared/tools/malfunctionreporter/slot-malfunction-reporter.component";
import {UpdateRequestToolComponent, UpdateRequestToolConfig} from '@webmate-web-core/shared/tools/updaterequest/update-request.component';
import {faExpand} from '@fortawesome/pro-light-svg-icons';
import {faPlug as faSolidPlug} from '@fortawesome/pro-solid-svg-icons';
import {faPlug as faRegularPlug} from '@fortawesome/pro-regular-svg-icons';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {Popover} from 'primeng/popover';
import {AdvancedOptionsPopoverCoordinatorService} from './advanced-options-popover-coordinator.service';
import {SlotReservationEditorComponent, SlotReservationEditorToolConfig} from './slot-reservation-editor.component';
import {
    DeployDeviceDialogComponent,
    DeployDeviceDialogToolConfig
} from '@webmate-web-core/shared/tools/device-deployment/deploy-device-dialog.component';
import {DeviceSlotDetailsComponent, DeviceSlotDetailsToolConfig} from './device-slot-details.component';

/** delay before we ask the backend a second time for the Deployables of a Slot that appears to offer nothing */
const DEPLOYABLE_RECHECK_DELAY_MS = 2000;

interface SlotAdvancedAction {
    id: string;
    label: string;
    icon: IconProp;
    disabled?: boolean;
    primary?: boolean;
    danger?: boolean;
    command: () => void;
}

@UntilDestroy()
@Component({
    selector: 'wm-deviceslot-summary',
    templateUrl: './deviceslot-summary.component.html',
    styleUrls: ['./deviceslot-summary.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceSlotSummaryComponent {

    genericEvent: Subject<WebmateEvent>;
    faExpand = faExpand;
    faSolidPlug = faSolidPlug;

    @Input() resourceType: string;
    @Input() resourceId: DeviceSlotId;
    @Input() projectId: ProjectId;
    @Input() viewType: string;
    @Input() params: any;

    ownUserId: UserId;

    deviceSlot?: DeviceSlot = undefined;
    deviceIsFromDifferentProject: boolean = false;
    deviceIdInSlot: any /* DeviceId or Null or Undefined */ = undefined;

    deployables: Deployable[] = [];
    advertisedDeployables: AdvertisedDeployable[];

    deviceInSlot: Device = null;
    projectBlockingSlot: Project;
    userBlockingSlot: UserInfo;

    reservations: Array<DeviceSlotReservation>;
    shownReservation: DeviceSlotReservation;
    userHavingReservation: UserInfo;
    mayReserveSlot: boolean = false;
    userOfReservations: { [s: string /* ReservationId */]: UserInfo } = {};
    mayRequestDeviceUpdate = false;

    userMayDeployDevices: boolean = false;
    userMayToggleDailyRedeployment: boolean = false;
    userMaySetStartupScripts: boolean = false;

    deploymentHintVisible: boolean = false;
    userWantsToDeploy: boolean = false;
    userMayClearSlotsNoMatterWhat: boolean = false;

    isAutomaticallyInUse: boolean = true; //the sane default is true!
    isReservedByAnotherUser: boolean = true; //dito
    isReservedByMyself: boolean = false; //the sane default is false
    otherUsers: Array<UserId> = [];
    otherUserInfo: UserInfo[] = [];
    isAccessedByDifferentUser: boolean = true; //the sane default is true!

    redeploymentTooltip: string;

    singleDeployableLogo: string;

    deviceSlotDetailsVisible: boolean = false;

    advancedActions: SlotAdvancedAction[] = [];

    constructor(private deviceSlotService: DeviceSlotService, private templateService: DeployableService,
                private platformLogoResolver: LogoResolverService, private userService: UserService,
                private deviceService: DeviceService, private featureService: FeatureService,
                private reservationService: DeviceSlotReservationService, private notificationService: WmNotificationService,
                private projectService: ProjectService, private modalToolService: ModalToolContainerService,
                private cdRef: ChangeDetectorRef,
                private advancedOptionsPopoverCoordinator: AdvancedOptionsPopoverCoordinatorService) {
    }

    private buildDropdownMenu() {
        this.advancedActions = [];

        if (!this.isReservedByMyself && this.userMayClearSlotsNoMatterWhat && (this.deviceIsFromDifferentProject || this.isReservedByAnotherUser)) {
            this.advancedActions.push({
                id: `slot-${this.deviceSlot.id}-clear-btn`,
                label: 'Force Device Release',
                icon: faRegularPlug,
                command: this.clearSlot.bind(this)
            });
        }

        if (this.userMaySetStartupScripts && this.deviceSlot?.isVCloudSlot()) {
            this.advancedActions.push({
                id: `slot-${this.deviceSlot.id}-startup-scripts-btn`,
                label: 'Manage Startup Scripts',
                icon: 'code',
                command: this.openScriptManagerTool.bind(this)
            });
        }

        this.advancedActions.push({
            id: `device-${this.deviceSlot.id}-details`,
            label: 'Show Details',
            icon: 'info-circle',
            command: this.openDeviceSlotDetails.bind(this)
        });

        this.advancedActions.push({
            id: `slot-${this.deviceSlot.id}-report-malfunction-btn`,
            label: 'Report malfunction',
            icon: 'exclamation-triangle',
            command: this.openSlotMalfunctionReportModal.bind(this)
        });

        if (this.mayRequestDeviceUpdate && this.deviceSlot!.isPrivateMobile()) {
            const updatePending = this.deviceSlot!.propertyBag?.updatePending === true;
            this.advancedActions.push({
                id: `slot-${this.deviceSlot.id}-request-update-btn`,
                label: updatePending ? 'Update already requested' : 'Request update',
                icon: 'arrow-circle-up',
                disabled: updatePending,
                command: this.openRequestUpdateModal.bind(this)
            });
        }

        if (!this.deviceSlot.availableForDeployment && this.mayReserveSlot && this.isReservedByMyself) {
            this.advancedActions.push({
                id: `slot-${this.deviceSlot.id}-clear-btn`,
                label: 'Claim Slot',
                icon: 'hand-paper',
                command: this.clearSlot.bind(this)
            });
        }
    }

    alignAdvancedOptionsArrow(popover: Popover, trigger: HTMLElement): void {
        const container = popover.container;
        if (!container) {
            return;
        }

        const containerBounds = container.getBoundingClientRect();
        const triggerBounds = trigger.getBoundingClientRect();
        const triggerCenter = triggerBounds.left + triggerBounds.width / 2 - containerBounds.left;
        const arrowLeft = Math.max(12, Math.min(containerBounds.width - 12, triggerCenter));
        container.style.setProperty('--device-advanced-options-arrow-left', `${arrowLeft}px`);
    }

    toggleAdvancedOptions(event: Event, popover: Popover, trigger: HTMLElement): void {
        this.advancedOptionsPopoverCoordinator.activate(popover);
        popover.toggle(event, trigger);
    }

    closeAdvancedOptions(popover: Popover): void {
        this.advancedOptionsPopoverCoordinator.release(popover);
    }

    executeAdvancedAction(action: SlotAdvancedAction, popover: Popover): void {
        popover.hide();
        action.command();
    }

    openReservationTool(): void {
        this.modalToolService.openTool(
            SlotReservationEditorComponent,
            new SlotReservationEditorToolConfig(this.deviceSlot, this.projectId)
        );
    }

    get showDailyRedeploymentToggle(): boolean {
        return this.deviceSlot !== undefined && !this.deviceSlot.isPublicSlot();
    }

    get dailyRedeploymentEnabled(): boolean {
        return this.deviceSlot?.isAutoRedeployEnabledForProject(this.projectId) ?? false;
    }

    get dailyRedeploymentToggleDisabled(): boolean {
        return !this.userMayToggleDailyRedeployment || this.isReservedByAnotherUser;
    }

    /** set when the Slot changed from 'occupied' to 'empty', in which case the cached Deployables are outdated */
    private slotWasEmptied = false;

    ngOnInit() {
        this.deviceService.handleDeviceSlotUpdateEvents().pipe(untilDestroyed(this)).subscribe(evt => {
            if (evt.deviceSlotId === this.resourceId) {
                this.refreshDeployables(true);
            }
        });

        let slotReservationFeature = this.featureService.getFeature('webmate.device-slot-reservations').pipe(tap(f => {
            if (f?.isActive) {
                this.mayReserveSlot = true;
            }
        }));

        let startupScriptFeature = this.featureService.getFeature('webmate.vmcustomization').pipe(tap(f => {
            if (f?.isActive) {
                this.userMaySetStartupScripts = true;
            }
        }));

        let deviceUpdateFeature = this.featureService.getFeature('webmate.request-device-update').pipe(tap(f => {
            if (f?.isActive) {
                this.mayRequestDeviceUpdate = true;
            }
        }));

        let features$ = combineLatest([slotReservationFeature, startupScriptFeature, deviceUpdateFeature]);

        let createDevicePermission = this.userService.hasPermissionInProject(this.projectId, CreateOrDeleteDevicePermission).pipe(tap(yesOrNo => this.userMayDeployDevices = yesOrNo));
        let devicePropertiesPermission = this.userService.hasPermissionInProject(this.projectId, SetDeviceSlotProperties).pipe(tap(yesOrNo => {
            this.userMayToggleDailyRedeployment = yesOrNo;
        }));
        let clearSlotPermission = this.userService.hasAdminPermission(ManageSlotsPermission).pipe(tap(yesOrNo => this.userMayClearSlotsNoMatterWhat = yesOrNo));

        let permissions$ = combineLatest([createDevicePermission, devicePropertiesPermission, clearSlotPermission]);

        features$.pipe(
            mergeMap(() => permissions$),
            mergeMap(() => this.userService.getCurrentUserData()),
            mergeMap(user => {
                this.ownUserId = user.id;
                return this.projectService.getProjectById$(this.projectId);
            }),
            mergeMap(project => {
                this.redeploymentTooltip = 'Daily redeployment is enabled';
                const redeployTime = project.properties['autoRedeployTime'];
                if (redeployTime) {
                    this.redeploymentTooltip += ` for ${redeployTime} CET`;
                }

                return this.deviceSlotService.getSlotInProject$(this.resourceId, this.projectId);
            }),
            mergeMap(next => {
                this.slotWasEmptied = !!(this.deviceSlot?.deviceId !== undefined && next.deviceId === undefined);
                this.deviceSlot = next;

                if (next.availableForDeployment) {
                    /** no Device exists, hence the Slot is unused and 'deviceIsFromDifferentProject' must be false */
                    this.deviceIdInSlot = undefined;
                    this.deviceIsFromDifferentProject = false;
                } else {
                    if (next.deviceId !== undefined) {
                        /** a known Device, either one in our Project or one from a Project were we are a Member! We will update 'deviceIsFromDifferentProject' by busy the Device */
                        this.deviceIdInSlot = next.deviceId;
                    } else {
                        /** an unknown device-id but Slot is blocked, that means 'deviceIsFromDifferentProject' must be true and we have to idea what Project or Device it is */
                        this.deviceIdInSlot = null;
                        this.deviceInSlot = null;
                        this.deviceIsFromDifferentProject = true;
                    }
                }

                if (this.mayReserveSlot) {
                    this.reservations = next.reservations;
                } else {
                    this.reservations = [];
                }
                this.reservations.forEach((reservation) =>
                    this.userService.getInformationAboutUser(reservation.userId).pipe(take(1)).subscribe((user) => this.userOfReservations[reservation.id] = user)
                );
                let activeReserv = this.reservations.find(r => r.isActive);
                this.shownReservation = activeReserv ?? this.reservations.toSorted(reservationSortingFunction)[0];
                this.isReservedByAnotherUser = activeReserv !== undefined && this.ownUserId !== activeReserv.userId;
                this.isReservedByMyself = activeReserv !== undefined && this.ownUserId === activeReserv.userId;
                return this.shownReservation !== undefined ? this.userService.getInformationAboutUser(this.shownReservation.userId) : observableOf(undefined);
            }), mergeMap(userHavingReservation => {
                this.userHavingReservation = userHavingReservation;
                return this.deviceSlot.projectBlockingSlot ? this.projectService.getProjectById$(this.deviceSlot.projectBlockingSlot).pipe(/*handle missing permissions on project*/ catchError(e => of(undefined))) : observableOf(undefined);
            }), mergeMap(p => {
                this.projectBlockingSlot = p;
                return this.deviceSlot.userBlockingSlot ? this.userService.getInformationAboutUser(this.deviceSlot.userBlockingSlot) : observableOf(undefined);
            }), mergeMap(u => {
                this.userBlockingSlot = u;
                if (this.deviceSlot.isVCloudSlot() || this.deviceSlot.isPublicSlot()) {
                    this.advertisedDeployables = [];
                    return observableOf([]);
                } else {
                    const deployablesAreOutdated = this.slotWasEmptied;
                    this.slotWasEmptied = false;
                    return this.templateService.getDeployablesForSlot$(this.deviceSlot.id, deployablesAreOutdated).pipe(map(d => d.sort(deployableSortingFunction)));
                }
            }), mergeMap(deployables => {
                // deployables have been updated
                if (deployables.length === 1) {
                    this.platformLogoResolver.getLogoForPlatform(deployables[0].platform).pipe(take(1)).subscribe(logo => this.singleDeployableLogo = logo);
                }
                this.deployables = deployables;
                return this.deviceSlot.deviceId !== undefined ? this.deviceService.getDevice$(this.deviceSlot.deviceId).pipe(/*handle missing permissions on device*/ catchError(e => of(undefined))) : observableOf(undefined);
            }),
            mergeMap(deviceInSlotOrUndefined => {
                this.deviceInSlot = deviceInSlotOrUndefined;
                this.deviceIsFromDifferentProject = !this.deviceSlot.availableForDeployment && (deviceInSlotOrUndefined === undefined || deviceInSlotOrUndefined.projectId !== this.projectId);
                this.isAccessedByDifferentUser = false;
                this.isAutomaticallyInUse = false;
                this.otherUsers = [];
                this.otherUserInfo = [];
                let userInfoObservables: Observable<UserInfo>[] = [];
                if (deviceInSlotOrUndefined) {
                    deviceInSlotOrUndefined.tickets.forEach(ticket => {
                        if (ticket.job.name === 'DeviceSession') {
                            return;
                        }
                        if (ticket.job.name === 'ConsoleAccess' && ticket.userId !== this.ownUserId) {
                            this.isAccessedByDifferentUser = true;
                            if (this.otherUsers.indexOf(ticket.userId) < 0) {
                                this.otherUsers.push(ticket.userId);
                                //this means the Users are not sorted, but what the hell...    too costly for such a minor UseCase
                                userInfoObservables.push(this.userService.getInformationAboutUser(ticket.userId));
                            }
                        }
                        if (ticket.job.properties.isAutomatedJob) {
                            // TODO distinguish the different use cases
                            this.isAutomaticallyInUse = true;
                        }
                    });
                }
                return (userInfoObservables.length > 0 ? observableCombineLatest(userInfoObservables) : observableOf([]));
            }), mergeMap(userInfos => {
                this.otherUserInfo = userInfos;
                return this.platformLogoResolver.getLogosForDeployables(this.deployables);
            }), untilDestroyed(this)).subscribe(logos => {
            this.advertisedDeployables = logos as AdvertisedDeployable[];
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
        });
    }

    /**
     * Re-reads the Deployables of this Slot from the backend, e.g. after a Device was released and the Slot became
     * usable again. If nothing is offered afterwards, we check once more after a short delay: the Slot-Event may
     * reach us before the backend has finished marking the Offer as available again.
     */
    private refreshDeployables(allowRecheck: boolean): void {
        if (this.deviceSlot === undefined || this.deviceSlot.isVCloudSlot() || this.deviceSlot.isPublicSlot()) {
            /* those Slots do not advertise their Deployables in this view, see 'ngOnInit' */
            return;
        }
        this.templateService.getDeployablesForSlot$(this.resourceId, true).pipe(
            take(1),
            map(deployables => deployables.slice().sort(deployableSortingFunction)),
            mergeMap(deployables => {
                this.deployables = deployables;
                if (deployables.length === 1) {
                    this.platformLogoResolver.getLogoForPlatform(deployables[0].platform).pipe(take(1)).subscribe(logo => this.singleDeployableLogo = logo);
                }
                return this.platformLogoResolver.getLogosForDeployables(deployables);
            }),
            untilDestroyed(this)
        ).subscribe(logos => {
            this.advertisedDeployables = logos as AdvertisedDeployable[];
            this.cdRef.markForCheck();
            if (allowRecheck && !this.deployables.some(deployable => isAvailable(deployable))) {
                timer(DEPLOYABLE_RECHECK_DELAY_MS).pipe(take(1), untilDestroyed(this)).subscribe(() => this.refreshDeployables(false));
            }
        });
    }

    getUserOfReservation(id: DeviceSlotReservationId): UserInfo {
        return this.userOfReservations[id];
    }

    getUpcomingReservations(): DeviceSlotReservation[] {
        return this.reservations.filter((reservation) => reservation.id !== this.shownReservation.id);
    }

    getPermanentIPInfo(): string /* or undefined */ {
        if (this.deviceSlot === undefined) return undefined;

        if (this.deviceSlot.propertyBag.permanent_properties !== undefined) {
            if (this.deviceSlot.propertyBag.permanent_properties['ip'] !== undefined) {
                return this.deviceSlot.propertyBag.permanent_properties['ip'];
            }
        }
        return undefined;
    }

    getPermanentProxy(): any {
        if (this.deviceSlot === undefined) return undefined;

        if (this.deviceSlot.propertyBag.permanent_properties !== undefined) {
            if (this.deviceSlot.propertyBag.permanent_properties['proxy'] !== undefined) {
                return this.deviceSlot.propertyBag.permanent_properties['proxy'];
            }
        }
        return undefined;
    }

    getAccessUsers(): string {
        let base = 'Device currently accessed by ';
        if (this.otherUserInfo.length == 1) {
            return base + this.otherUserInfo[0].name;
        } else {
            let msg = base + this.otherUserInfo.map(userInfo => userInfo.name).join(', ');
            let pos = msg.lastIndexOf(',');
            return msg.substring(0, pos) + ' and' + msg.substring(pos + 1);
        }
    }

    getAutoRedeployStatus(): string {
        return this.deviceSlot !== undefined ? (this.deviceSlot.isAutoRedeployEnabledForProject(this.projectId) ? 'redeployed daily' : 'no daily redeployment') : '...';
    }

    prettyPrintRequirements(): string {
        return JSON.stringify(this.deviceSlot.deviceRequirement);
    }

    private getSlotPlatformFamily(): PlatformFamily {
        if (this.deviceSlot.isPublicSlot() || this.deviceSlot.isPrivateMobile()) {
            return 'mobile';
        }
        if (this.deviceSlot.isVCloudSlot()) {
            return 'desktop';
        }
        /** the Slot itself knows its Platform, so do not guess from the Deployables while those are still loading */
        const requiredPlatformType = this.deviceSlot.deviceRequirement[DevicePropertyNames.MachinePlatformType];
        if (typeof requiredPlatformType === 'string') {
            return getPlatformFamily({[DevicePropertyNames.MachinePlatformType]: requiredPlatformType});
        }
        const firstDeployable = this.deployables[0];
        return firstDeployable ? getPlatformFamily(firstDeployable.properties) : 'desktop';
    }

    /**
     * Whether the Deployables of this Slot are known. As long as they are not, the Slot shows a spinner and the
     * Deploy-Button must stay disabled: an early click would neither find the single Deployable to deploy right
     * away nor could the dialog be opened with the correct Platform-Family.
     */
    get deployablesLoaded(): boolean {
        return this.advertisedDeployables !== undefined;
    }

    deployDevice() {
        if (!this.userMayDeployDevices || this.isReservedByAnotherUser || !this.deployablesLoaded) return;
        if (this.deployables.length === 1) {
            /** there is nothing to choose from, so deploy the single device right away instead of showing the dialog */
            this.deploySingleDeployable(this.deployables[0]);
            return;
        }
        this.modalToolService.openTool(DeployDeviceDialogComponent, new DeployDeviceDialogToolConfig({
            projectId: this.projectId,
            platformFamily: this.getSlotPlatformFamily(),
            selectedGroupingVariant: 'os',
            browserSessionDefault: false,
            includeDeployedDevices: false,
            showDeployAndOpen: true,
            preFilter: deployable => deployable.properties[DevicePropertyNames.SlotId] === this.deviceSlot.id
        }));
    }

    private deploySingleDeployable(deployable: Deployable) {
        this.userWantsToDeploy = true;
        this.deploymentHintVisible = true;
        const requirements = constructRequirementsForDeployableInSlot(deployable, this.deviceSlot.id, false, undefined);
        this.deviceService.requestDeployment$(this.projectId, requirements, deployable.modelName).pipe(
            untilDestroyed(this)
        ).subscribe(() => {
            this.userWantsToDeploy = false;
            this.cdRef.markForCheck();
        }, err => {
            const msg = 'Request for deployment failed, error was: ' + ((typeof (err['error']) === 'string' ? err['error'] : undefined) || 'unknown');
            console.error(msg, err);
            this.userWantsToDeploy = false;
            this.cdRef.markForCheck();
            this.notificationService.toastWarningNotification(msg);
        });
    }

    explainReservation() {
        this.notificationService.toastNotification(this.deviceSlot.name + ' has been reserved by ' + this.userHavingReservation.name + '. It cannot be used by anyone else until the reservation expires or is revoked by that user or an administrator.');
    }

    showDeploymentHint() {
        this.deploymentHintVisible = (this.userMayDeployDevices && !this.isReservedByAnotherUser) || this.userWantsToDeploy;
    }

    hideDeploymentHint() {
        this.deploymentHintVisible = this.userWantsToDeploy;
    }

    getSlotTechnicalInfo(getDescNotValue: boolean): string {
        /** check the permanent bag first */
        if (this.deviceSlot.propertyBag.permanent_properties !== undefined) {
            let ip = this.deviceSlot.propertyBag.permanent_properties['ip'];
            if (ip !== undefined) {
                return getDescNotValue ? 'Associated IP:' : ip;
            }
        }
        if (this.deviceSlot.propertyBag.temporary_properties !== undefined) {
            let ip = this.deviceSlot.propertyBag.temporary_properties['ip'];
            if (ip !== undefined) {
                return getDescNotValue ? 'IP in BrowserCenter:' : ip;
            }
        }

        //TODO: other properties for other Slots?

        return undefined;
    }

    toggleAutoRedeploymentForSlot() {
        if (!this.userMayToggleDailyRedeployment) return;
        let newVal = !this.deviceSlot.isAutoRedeployEnabledForProject(this.projectId);
        this.deviceSlotService.updateAutoRedeploymentOfSlotInProject(this.projectId, this.resourceId, newVal).subscribe(
            next => {
                this.deviceSlot = this.deviceSlot.copyWithNewAutoRedeployEnabledForProject(newVal, this.projectId);
                this.buildDropdownMenu();
                this.cdRef.markForCheck();
            },
            err => {
                this.notificationService.toastWarningNotification('Toggling the Redeployment-Setting for the Slot ' + this.deviceSlot.name + ' failed due to an unexpected Error');
            }
        );
    }

    toggleDeviceSlotDetails(): void {
        this.deviceSlotDetailsVisible = !this.deviceSlotDetailsVisible;
        this.cdRef.markForCheck();
    }

    openDeviceSlotDetails(): void {
        this.modalToolService.openTool(DeviceSlotDetailsComponent, new DeviceSlotDetailsToolConfig(
            this.deviceSlot,
            this.projectId,
            this.deviceInSlot,
            this.deviceIsFromDifferentProject,
            this.projectBlockingSlot,
            this.userBlockingSlot,
            this.shownReservation,
            this.isReservedByMyself,
            this.isReservedByAnotherUser,
            this.userHavingReservation,
            (this.reservations ?? []).filter(reservation => reservation.id !== this.shownReservation?.id),
            this.userOfReservations
        ));
    }

    clearSlot(): void {
        let request = () => {
            this.deviceSlot = this.deviceSlot.copyWithNewSlotReleaseStatus(true);
            this.buildDropdownMenu();
            this.cdRef.markForCheck();
            this.deviceSlotService.clearSlot(this.resourceId).subscribe(
                next => {/* nothing, wait for event */
                },
                err => {
                    this.deviceSlot = this.deviceSlot.copyWithNewSlotReleaseStatus(false);
                    this.buildDropdownMenu();
                    this.cdRef.markForCheck();
                    this.notificationService.toastWarningNotification('Clearing/Claiming the Slot ' + this.deviceSlot.name + ' failed due to an unexpected Error');
                }
            );
        };
        this.notificationService.open(this.isReservedByMyself ? 'You have a reservation for this slot now but someone else is using it. You can enforce your reservation now, thereby stopping and deleting the device of the other user. Continue?' : 'As an administrator, you can forcefully clear this slot, deleting whatever device is currently contained within. Note that this might disrupt the workflow of other users. Continue?', this.isReservedByMyself ? 'Claim slot' : 'Clear slot', request, 'Cancel');
    }

    private versionClipCache = {};

    clipVersionNumber(version: string) {
        if (!this.versionClipCache[version]) {
            this.versionClipCache[version] = version.split('.')[0];
        }
        return this.versionClipCache[version];
    }

    getSlotTypeTooltip(): string {
        if (this.deviceSlot?.isPublicSlot()) {
            return 'Public mobile device slot';
        }
        if (this.deviceSlot?.isVCloudSlot() && this.deviceSlot.isWindowsSlot()) {
            return this.deviceSlot.isCustomSlot() ? 'Custom Windows virtual machine slot' : 'Windows virtual machine slot';
        }
        if (this.deviceSlot?.isVCloudSlot() && this.deviceSlot.isMacOSSlot()) {
            return this.deviceSlot.isCustomSlot() ? 'Custom macOS virtual machine slot' : 'macOS virtual machine slot';
        }
        if (this.deviceSlot?.isVCloudSlot()) {
            return this.deviceSlot.isCustomSlot() ? 'Custom virtual machine slot' : 'Virtual machine slot';
        }
        if (this.advertisedDeployables === undefined) {
            return 'Device slot';
        }
        if (this.advertisedDeployables.length === 0) {
            return 'Slot not usable';
        }
        if (this.advertisedDeployables.length === 1) {
            return `${this.advertisedDeployables[0].displayName} slot`;
        }
        return `${this.advertisedDeployables.length} compatible device types`;
    }

    hasAnnotations(d: Deployable) {
        return !!d.properties[DevicePropertyNames.Annotations];
    }

    getAnnotationStatus(d: Deployable) {
        return d.properties[DevicePropertyNames.Annotations]['status'];
    }

    getAnnotationString(d: Deployable) {
        let msg = d.properties[DevicePropertyNames.Annotations]['msg'];
        return capitalize(this.getAnnotationStatus(d)) + (!!msg ? ': ' + msg : '');
    }

    devicesDefect() {
        return this.deployables && this.deployables.length === 1 && ((this.hasAnnotations(this.deployables[0]) && this.getAnnotationStatus(this.deployables[0]) === 'defect') || !isAvailable(this.deployables[0]));
    }

    tooltipForDeployButton(): string {
        if (!this.userMayDeployDevices) {
            return 'You are not allowed to deploy devices in this project';
        } else if (!this.deployablesLoaded) {
            return 'Loading available devices…';
        } else if (this.deployables && this.devicesDefect()) {
            if (this.deployables.length === 1) {
                return this.hasAnnotations(this.deployables[0]) && this.getAnnotationStatus(this.deployables[0]) === 'defect'
                    ? 'Device can not be deployed as it is currently defect and will be replaced or repaired soon'
                    : 'Device is currently disconnected, we are working on restoring the connection';
            } else if (this.deployables.length > 0 && !isAvailable(this.deployables[0])) {
                return 'No devices available for deployment in this slot'
            }
        }
        return 'Deploy a new device in this slot';
    }

    hasUserAnnotations(d: Deployable) {
        return !!d.properties[DevicePropertyNames.UserAnnotations];
    }

    getUserAnnotationString(d: Deployable) {
        return d.properties[DevicePropertyNames.UserAnnotations]['msg'];
    }

    openScriptManagerTool() {
        this.modalToolService.openTool(ScriptManagerComponent, new ScriptManagerToolConfig(this.resourceId, this.projectId));
    }

    openSlotMalfunctionReportModal() {
        this.modalToolService.openTool(SlotMalfunctionReporterComponent, new SlotMalfunctionReporterToolConfig(this.resourceId, this.projectId));
    }

    openRequestUpdateModal() {
        // Both assignments below assume a single device private mobile slot
        const serial = this.deviceSlot!.deviceRequirement[DevicePropertyNames.OpenSTFSerial];
        const currentVersion = this.deployables[0]!.platform.getFullName();
        const modelName = this.deployables[0]!.modelName;
        this.modalToolService.openTool(UpdateRequestToolComponent, new UpdateRequestToolConfig(this.deviceSlot!, serial, modelName, currentVersion, this.projectId));
    }

}
